import React, { useState } from 'react';
import { useSecurity } from '@/contexts/SecurityContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

// Session timeout warning banner (F1.6)
export function SessionWarningBanner() {
  const { showTimeoutWarning, extendSession } = useSecurity();
  const [extending, setExtending] = useState(false);

  if (!showTimeoutWarning) return null;

  const handleExtend = async () => {
    setExtending(true);
    await extendSession();
    setExtending(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed top-0 left-0 right-0 z-[9998] bg-amber-50 dark:bg-amber-950/90 border-b border-amber-200 dark:border-amber-800 px-4 py-3"
      data-testid="session-warning-banner"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Your session will expire in 5 minutes. Click here to stay logged in.
          </p>
        </div>
        <Button
          onClick={handleExtend}
          disabled={extending}
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg shrink-0 min-h-[36px]"
          data-testid="extend-session-btn"
        >
          {extending ? 'Extending...' : 'Stay Logged In'}
        </Button>
      </div>
    </motion.div>
  );
}

// Tab blur overlay with click to restore (F1.1, F1.2)
export function TabBlurOverlay() {
  const { tabBlurred, handleOverlayClick } = useSecurity();
  const [attempting, setAttempting] = useState(false);

  if (!tabBlurred) return null;

  const handleClick = async () => {
    setAttempting(true);
    await handleOverlayClick();
    setAttempting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleClick}
      className="fixed inset-0 z-[9997] bg-slate-900/95 backdrop-blur-xl flex items-center justify-center cursor-pointer"
      data-testid="tab-blur-overlay"
    >
      <div className="text-center pointer-events-none">
        <Lock className="w-12 h-12 text-white/70 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Session Protected</h2>
        <p className="text-white/70 text-sm">
          {attempting ? 'Restoring session...' : 'Click anywhere to continue'}
        </p>
      </div>
    </motion.div>
  );
}

// Re-authentication modal with password entry (F1.4)
export function ReauthModal() {
  const { showReauthModal, onReauthSuccess, onReauthFailure, handleFullExpiry, failedAttempts, setShowReauthModal } = useSecurity();
  const { user, login } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!showReauthModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email || !password) return;

    setLoading(true);
    setError('');

    try {
      // Use the login function to re-authenticate
      const success = await login(user.email, password);
      if (success) {
        setPassword('');
        onReauthSuccess();
      } else {
        setError('Incorrect password. Please try again.');
        onReauthFailure();
      }
    } catch (err) {
      setError('Incorrect password. Please try again.');
      onReauthFailure();
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    setShowReauthModal(false);
    handleFullExpiry();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="reauth-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[var(--safekept-card)] rounded-2xl p-6 w-full max-w-md shadow-xl"
      >
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-[var(--safekept-text)]">Session Expired</h2>
          <p className="text-sm text-[var(--safekept-text-secondary)] mt-2">
            Please enter your password to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-[var(--safekept-text-secondary)]">Email</label>
            <Input
              type="email"
              value={user?.email || ''}
              disabled
              className="mt-1 min-h-[44px] rounded-xl bg-slate-50 dark:bg-slate-800"
            />
          </div>

          <div>
            <label className="text-sm text-[var(--safekept-text-secondary)]">Password</label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="min-h-[44px] rounded-xl pr-10"
                autoFocus
                data-testid="reauth-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {failedAttempts >= 3 && (
            <p className="text-xs text-[var(--safekept-text-secondary)]">
              {5 - failedAttempts} attempt(s) remaining before automatic logout.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleGoToLogin}
              className="flex-1 rounded-xl min-h-[44px]"
            >
              Go to Login
            </Button>
            <Button
              type="submit"
              disabled={loading || !password}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-xl min-h-[44px]"
              data-testid="reauth-submit"
            >
              {loading ? 'Verifying...' : 'Continue'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Combined security modals export
export default function SecurityModals() {
  return (
    <AnimatePresence>
      <SessionWarningBanner key="session-warning-banner" />
      <TabBlurOverlay key="tab-blur-overlay" />
      <ReauthModal key="reauth-modal" />
    </AnimatePresence>
  );
}
